import React from 'react';
import styled from 'styled-components/macro';
import { useObserver } from 'mobx-react-lite';
import { ProductAwardProps } from '../types/ProductAwardProps';
import { TypeProductAwardRatingDisplay } from '../types/TypeProductAwardRatingDisplay';
import { StarRatingProps } from '../types/StarRatingProps';

const UnstyledStarRating: React.FC<StarRatingProps> = ({
  className,
  rating,
  ratingFrom,
}) => {
  return useObserver(() => (
    <div
      className={'ww-star-rating ' + (className ?? '')}
      aria-label={rating + ' / ' + ratingFrom + ' Stars'}
    >
      {new Array(Math.floor(rating)).fill(0).map((_, i) => (
        <i className="icon-star" key={'star-' + i}></i>
      ))}
      {rating % 1 > 0.3 && <i className="icon-star-half"></i>}
    </div>
  ));
};

const StarRating = styled(UnstyledStarRating)`
  .icon-star,
  .icon-star-half {
    color: var(--star);
  }
`;

const UnstyledProductAward: React.FC<ProductAwardProps> = ({
  className,
  data,
}) => {
  return useObserver(() => (
    <div className={'ww-product-award ' + (className ?? '')}>
      {data.image && (
        <div className="award-image-container">
          <img src={data.image} alt={data.title || 'Award'} />
        </div>
      )}
      <div className="award-details">
        {data.title && <div className="award-title">{data.title}</div>}
        <div className="award-rating-display">
          {data.ratingText || ''}
          {data.ratingDisplay === TypeProductAwardRatingDisplay.Percentage &&
            data.rating && (
              <span className="award-rating-numeric">
                {((data.rating * 100) / (data.ratingFrom || 100)).toFixed(0)}%
              </span>
            )}
          {data.ratingDisplay === TypeProductAwardRatingDisplay.Fraction &&
            data.rating && (
              <span className="award-rating-numeric">
                {data.rating} {data.ratingFrom && <> / {data.ratingFrom}</>}{' '}
              </span>
            )}
          {data.ratingDisplay === TypeProductAwardRatingDisplay.Stars &&
            data.rating && (
              <StarRating
                rating={data.rating}
                ratingFrom={data.ratingFrom || 5}
              />
            )}
        </div>
        {data.description && (
          <div className="award-description">{data.description}</div>
        )}
        {data.awardDateDescription && (
          <div className="award-date-description">
            {data.awardDateDescription}
          </div>
        )}
      </div>
    </div>
  ));
};

export const ProductAward = styled(UnstyledProductAward)`
  display: flex;
  flex-flow: row nowrap;
  margin: 0 0 15px 0;

  .award-image-container {
    width: 50px;
    margin-right: 10px;
    text-align: center;
    flex-shrink: 0;
  }

  .award-image-container img {
    margin: auto;
  }

  .award-title {
    font-weight: bold;
  }

  .award-description,
  .award-date-description {
    font-style: italic;
    color: var(--gray-6, #888888);
  }

  .award-rating-display i {
    font-size: 1.3em;
  }

  .award-rating-numeric {
    font-weight: bold;
  }
`;
